import React, { useRef, useState } from "react";

import { ImageIcon } from "@/components/icons";

import { dayIndex, type DayRange } from "../utils/dayAxis";
import {
  formatCampaignDates,
  parseGraphTime,
  type CampaignDay,
  type CampaignEvent,
  type DayPost,
  type PostDay,
} from "../utils/dayMarkers";
import { MarkerCardPortal, useMarkerCard } from "./MarkerCard";
import { PlatformMark, ReportPostDrawer } from "./ReportPostDrawer";
import { SOURCE_COLOR } from "./ReportViz";

/** One day's pip: its dots bottom up, what the day holds in a few words, and
 *  the posts and campaign starts and ends its card lists. */
export interface DayPip {
  /** The Rome day, "YYYY-MM-DD". */
  date: string;
  dots: ReadonlyArray<{ key: string; color: string; boosted: boolean }>;
  summary: string;
  posts: ReadonlyArray<DayPost>;
  events: ReadonlyArray<CampaignEvent>;
}

interface DayPipsProps {
  pips: ReadonlyArray<DayPip>;
  /** The chart's days: a pip sits at its date's index, none outside. */
  range: DayRange;
  /** The x axis's domain in indexes, first and last. */
  domain: [number, number];
  /** The plot area's distance from the chart box: left past the y axis,
   *  right to the margin, bottom to the x axis line. */
  inset: { left: number; right: number; bottom: number };
}

const MAX_DOTS = 3;
const MAX_ROWS = 4;

const DAY_LABEL = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Rome",
  weekday: "short",
  day: "numeric",
  month: "short",
});
export const TIME_LABEL = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Rome",
  hour: "2-digit",
  minute: "2-digit",
});

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** A boosted post's dot: the social blue in a paid amber ring, white on
 *  both sides of the ring so it reads as a ring on an amber stack too. */
const BOOSTED_RING = `0 0 0 1.5px #FFFFFF, 0 0 0 3.5px ${SOURCE_COLOR.paid}, 0 0 0 5px #FFFFFF`;

/** A pip on the x axis of a day chart for each marked day, one dot per mark
 *  up to three. Hover, focus or tap opens a card with the day's rows; a post
 *  row opens the post in a drawer, and closing it returns focus to the pip. */
export const DayPips: React.FC<DayPipsProps> = ({ pips, range, domain, inset }) => {
  const card = useMarkerCard();
  const [drawerPost, setDrawerPost] = useState<DayPost | null>(null);
  // The pip the drawer opened from; focus goes back to it on close.
  const drawerFrom = useRef<HTMLElement | null>(null);

  const [first, last] = domain;
  const span = Math.max(last - first, 1);
  const openPip = card.open ? pips.find((p) => p.date === card.open?.key) : undefined;
  const dayLabel = (date: string) => {
    const [year, month, day] = date.split("-").map(Number);
    return DAY_LABEL.format(Date.UTC(year, month - 1, day, 12));
  };

  const openPost = (post: DayPost) => {
    if (!card.open) return;
    drawerFrom.current = card.open.anchor;
    setDrawerPost(post);
    card.close();
  };

  return (
    <>
      <div
        className="absolute h-0 pointer-events-none"
        style={{ left: inset.left, right: inset.right, bottom: inset.bottom }}
      >
        {pips.map(({ date, dots, summary }) => {
          const at = dayIndex(range, date);
          if (at == null || at < first || at > last) return null;
          const isOpen = card.open?.key === date;
          const ringed = dots.some((dot) => dot.boosted);
          return (
            <button
              key={date}
              type="button"
              aria-label={`${summary} on ${dayLabel(date)}`}
              className={`absolute -bottom-[10px] w-[20px] -translate-x-1/2 flex flex-col-reverse items-center ${
                ringed ? "gap-[8px] pt-[7px] pb-[8px] -mb-[2px]" : "gap-[3px] pt-[4px] pb-[6px]"
              } rounded-full pointer-events-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
              style={{ left: `${((at - first) / span) * 100}%` }}
              {...card.triggerProps(date)}
            >
              {dots.map((dot) => (
                <span
                  key={dot.key}
                  className={`block w-[8px] h-[8px] rounded-full transition-transform ${
                    dot.boosted ? "" : "ring-2 ring-white"
                  } ${isOpen ? "scale-125" : ""}`}
                  style={{
                    backgroundColor: dot.color,
                    boxShadow: dot.boosted ? BOOSTED_RING : undefined,
                  }}
                />
              ))}
            </button>
          );
        })}
      </div>
      {card.open && openPip && (
        <MarkerCardPortal
          anchor={card.open.anchor}
          cardProps={card.cardProps}
          label={`${dayLabel(openPip.date)}: ${openPip.summary}`}
        >
          <PipCard pip={openPip} dayLabel={dayLabel(openPip.date)} onOpenPost={openPost} />
        </MarkerCardPortal>
      )}
      <ReportPostDrawer
        post={drawerPost}
        onClose={() => setDrawerPost(null)}
        onCloseAutoFocus={(event) => {
          const pip = drawerFrom.current;
          if (!pip?.isConnected) return;
          event.preventDefault();
          card.focusQuietly(pip);
        }}
      />
    </>
  );
};

const PipCard: React.FC<{
  pip: DayPip;
  dayLabel: string;
  onOpenPost: (post: DayPost) => void;
}> = ({ pip, dayLabel, onOpenPost }) => {
  const shownPosts = pip.posts.slice(0, MAX_ROWS);
  const shownEvents = pip.events.slice(0, Math.max(MAX_ROWS - shownPosts.length, 1));
  const hidden = pip.posts.length + pip.events.length - shownPosts.length - shownEvents.length;
  return (
    <>
      <div className="flex items-baseline justify-between gap-2 mb-1.5 text-[11px]">
        <span className="font-medium text-gray-900">{dayLabel}</span>
        <span className="text-gray-500 truncate">{pip.summary}</span>
      </div>
      <ul className="space-y-0.5">
        {shownPosts.map((post) => (
          <PostRow key={post.key} post={post} onOpen={() => onOpenPost(post)} />
        ))}
        {shownEvents.map((event, i) => (
          <CampaignRow key={`campaign-${i}`} event={event} />
        ))}
      </ul>
      {hidden > 0 && <p className="mt-1.5 text-[11px] text-gray-500">and {hidden} more</p>}
    </>
  );
};

/** The pips for a day chart: posts in the social blue, a boosted one ringed
 *  in the paid amber, and campaign starts and ends in the paid amber. A day
 *  holding both is one pip, its card listing the posts first, and keeps a
 *  dot of each kind. */
export const dayPips = (
  posts: ReadonlyArray<PostDay> = [],
  campaigns: ReadonlyArray<CampaignDay> = [],
): DayPip[] => {
  const postsOn = new Map(posts.map((d) => [d.date, d.posts]));
  const eventsOn = new Map(campaigns.map((d) => [d.date, d.events]));
  const dates = Array.from(
    new Set([...posts.map((d) => d.date), ...campaigns.map((d) => d.date)]),
  ).sort();
  return dates.map((date) => {
    const dayPosts = postsOn.get(date) ?? [];
    const events = eventsOn.get(date) ?? [];
    const postDots = Math.min(dayPosts.length, events.length > 0 ? MAX_DOTS - 1 : MAX_DOTS);
    const eventDots = Math.min(events.length, MAX_DOTS - postDots);
    return {
      date,
      dots: [
        ...dayPosts.slice(0, postDots).map((post) => ({
          key: post.key,
          color: SOURCE_COLOR.social,
          boosted: post.boost != null,
        })),
        ...events.slice(0, eventDots).map((_, i) => ({
          key: `campaign-${i}`,
          color: SOURCE_COLOR.paid,
          boosted: false,
        })),
      ],
      summary: [
        dayPosts.length > 0 ? plural(dayPosts.length, "post") : null,
        events.length > 0 ? plural(events.length, "campaign change") : null,
      ]
        .filter(Boolean)
        .join(" · "),
      posts: dayPosts,
      events,
    };
  });
};

/** A post's small image, the image icon when it has none or it fails. */
const PostThumbnail: React.FC<{ url: string | null; className: string; iconClassName: string }> = ({
  url,
  className,
  iconClassName,
}) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className={`shrink-0 overflow-hidden bg-gray-100 flex items-center justify-center ${className}`}>
      {url && !broken ? (
        <img src={url} alt="" className="w-full h-full object-cover" onError={() => setBroken(true)} />
      ) : (
        <ImageIcon className={`${iconClassName} text-gray-400`} />
      )}
    </span>
  );
};

const PostRow: React.FC<{ post: DayPost; onOpen: () => void }> = ({ post, onOpen }) => {
  const time = parseGraphTime(post.publishedAt);
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-[calc(100%+12px)] -mx-1.5 px-1.5 py-1 flex items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <PostThumbnail url={post.thumbnailUrl} className="w-[40px] h-[40px] rounded-md" iconClassName="w-4 h-4" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
            {post.platforms.map((key) => (
              <PlatformMark key={key} platform={key} />
            ))}
            {!Number.isNaN(time) && <span className="tabular-nums">{TIME_LABEL.format(time)}</span>}
          </span>
          <span className="block text-[12px] tabular-nums">
            <span className="font-medium text-gray-900">{post.engagement.toLocaleString()}</span>
            <span className="text-gray-500"> engagement</span>
          </span>
          {post.boost && (
            <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
              <span
                className="inline-block w-[6px] h-[6px] rounded-full"
                style={{ backgroundColor: SOURCE_COLOR.paid }}
              />
              Boosted · {formatCampaignDates(post.boost.startDate, post.boost.endDate)}
            </span>
          )}
          {post.caption && (
            <span className="block text-[11px] text-gray-400 truncate">{post.caption}</span>
          )}
        </span>
      </button>
    </li>
  );
};

// "PERFORMANCE_MAX" reads "Performance max".
export const channelLabel = (channelType: string): string => {
  const words = channelType.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A campaign start or end: no thumbnail, the paid dot where a post has its
 *  image, then the name over what happened and where. */
const CampaignRow: React.FC<{ event: CampaignEvent }> = ({ event }) => (
  <li className="flex items-start gap-2.5 py-1">
    <span className="shrink-0 w-[40px] flex justify-center pt-[5px]">
      <span
        className="block w-[8px] h-[8px] rounded-full"
        style={{ backgroundColor: SOURCE_COLOR.paid }}
      />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[12px] font-medium text-gray-900 truncate">
        {event.campaignName}
      </span>
      <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
        {event.kind === "started" ? "Started" : "Ended"}
        <span aria-hidden="true">·</span>
        {event.platform && <PlatformMark platform={event.platform.toLowerCase()} />}
        {event.channelType && <span>{channelLabel(event.channelType)}</span>}
      </span>
    </span>
  </li>
);
